import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

/** The two color modes of the Hyper layout (`data-bs-theme`). */
export type ColorTheme = 'light' | 'dark';

/**
 * Where the choice is remembered — the product's key from before the chat app, so saved
 * choices carry over. Each origin keeps its own copy (storage is per origin); the chat
 * app also receives the product's theme on the sign-in handoff, and its `index.html`
 * reads this same key to paint the theme before the first frame.
 */
export const COLOR_THEME_STORAGE_KEY = 'preferred-theme';

/** A valid theme, or null — for values read from storage or from a URL. */
export function parseColorTheme(value: unknown): ColorTheme | null {
  return value === 'light' || value === 'dark' ? value : null;
}

/** The theme the page is painted in right now. */
export function currentColorTheme(): ColorTheme {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
}

/**
 * Light/dark theme of the Hyper layout, shared by the product and the chat app (DEV-2062).
 * The theme is `data-bs-theme` on <html>; `data-menu-color` follows it for the product's
 * sidebar.
 */
@Injectable({
  providedIn: 'root',
})
export class ColorThemeService {

  private readonly renderer: Renderer2;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  get current(): ColorTheme {
    return currentColorTheme();
  }

  /** Paints the remembered choice — light when there is none. */
  init(): void {
    this.apply(this.restore() ?? 'light');
  }

  toggle(): void {
    this.apply(this.current === 'dark' ? 'light' : 'dark');
  }

  apply(theme: ColorTheme): void {
    this.renderer.setAttribute(document.documentElement, 'data-bs-theme', theme);
    this.renderer.setAttribute(document.documentElement, 'data-menu-color', theme);
    try {
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, theme);
    } catch {
      // Storage unavailable (private mode) — the theme lasts the page.
    }
  }

  private restore(): ColorTheme | null {
    try {
      return parseColorTheme(localStorage.getItem(COLOR_THEME_STORAGE_KEY));
    } catch {
      return null;
    }
  }
}
